import { useCallback, useState } from 'react';
import { IPostTariff } from '../../../../interfaces';

export interface IStepperFormData {
    id?: string;
    name: string;
    description: string;
    companyId?: string;
    companyIds: number[];
    initialTariffPackagesIds?: number[];
    outOfBundle: boolean;
    planType?: string;
    price?: number;
    priceCents?: number;
    productId?: string;
    tariffPackageIds: number[];
}

export interface IStepperState {
    formData: IStepperFormData;
    isSubmitting: boolean;
    updateFormData: (updates: Partial<IStepperFormData>) => void;
    resetFormData: () => void;
    initializeFormData: (initialData: Partial<IStepperFormData>) => void;
    getPostTariffData: () => IPostTariff;
    setSubmitting: (submitting: boolean) => void;
}

const defaultFormData: IStepperFormData = {
    name: '',
    price: 0,
    priceCents: 0,
    outOfBundle: false,
    tariffPackageIds: [],
    companyIds: [],
    description: '',
    companyId: '',
};

export const useStepperState = (initialData?: Partial<IStepperFormData>): IStepperState => {
    const [formData, setFormData] = useState<IStepperFormData>({
        ...defaultFormData,
        ...initialData,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const updateFormData = useCallback((updates: Partial<IStepperFormData>) => {
        setFormData((prev) => ({
            ...prev,
            ...updates,
        }));
    }, []);

    const resetFormData = useCallback(() => {
        setFormData(defaultFormData);
    }, []);

    const initializeFormData = useCallback((initialData: Partial<IStepperFormData>) => {
        setFormData({
            ...defaultFormData,
            ...initialData,
        });
    }, []);

    const getPostTariffData = useCallback((): IPostTariff => {
        return {
            name: formData.name,
            priceCents: formData.price ? formData.price * 100 : 0,
            outOfBundle: formData.outOfBundle,
            productId: formData.productId,
            planType: formData.planType,
            tariffPackageIds: formData.tariffPackageIds,
            companyIds: formData.companyIds,
            description: formData.description,
            initialTariffPackagesIds: formData.initialTariffPackagesIds || [],
            companyId: formData.companyId || '',
        };
    }, [formData]);

    const setSubmitting = useCallback((submitting: boolean) => {
        setIsSubmitting(submitting);
    }, []);

    return {
        formData,
        isSubmitting,
        updateFormData,
        resetFormData,
        initializeFormData,
        getPostTariffData,
        setSubmitting,
    };
};
